"use client"

/*
  Toast ENGINE = @owlcat/ui sonner (fleet baseline). The former radix
  toast/toaster/use-toast implementation is removed; this module is now a thin
  COMPAT layer that maps the shop's historical `toast({ title, description,
  variant })` + `toast.info/success/warning/error` API onto sonner, so the
  existing call sites keep working with zero churn. Mount <Toaster /> (the sonner
  one, re-exported from components/ui/toaster) once in the root layout.
*/

import type * as React from "react"
import { toast as sonnerToast } from "@owlcat/ui"

export type ToastVariant = "default" | "destructive"

export interface ToastInput {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: ToastVariant
  duration?: number
}

type Kind = "default" | "info" | "success" | "warning" | "error"

function emit(kind: Kind, input: ToastInput | string) {
  const props: ToastInput = typeof input === "string" ? { title: input } : input
  const { title, description, variant, duration } = props
  const message = (title ?? description ?? "") as React.ReactNode
  // If both title + description are present, title is the heading and
  // description is the sub-line; otherwise the single field is the message.
  const opts = {
    description: title ? description : undefined,
    duration,
  }
  const effective: Kind =
    kind === "default" && variant === "destructive" ? "error" : kind

  let id: string | number
  switch (effective) {
    case "info":
      id = sonnerToast.info(message, opts)
      break
    case "success":
      id = sonnerToast.success(message, opts)
      break
    case "warning":
      id = sonnerToast.warning(message, opts)
      break
    case "error":
      id = sonnerToast.error(message, opts)
      break
    default:
      id = sonnerToast(message, opts)
  }

  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    update: () => {
      /* no-op: sonner has no imperative update in this compat surface */
    },
  }
}

const createToast = (input: ToastInput | string) => emit("default", input)

// Convenience methods preserved for Medusa UI / legacy call-site compatibility.
const toast = Object.assign(createToast, {
  info: (input: ToastInput | string) => emit("info", input),
  success: (input: ToastInput | string) => emit("success", input),
  warning: (input: ToastInput | string) => emit("warning", input),
  error: (input: ToastInput | string) => emit("error", input),
})

function useToast() {
  return {
    toast,
    // Sonner owns the toast queue/rendering; nothing to expose here.
    toasts: [] as never[],
    dismiss: (id?: string | number) => sonnerToast.dismiss(id),
  }
}

export { useToast, toast }
